import { FormControl, FormControlHelperText } from '@/components/ui/form-control';
import { HStack } from '@/components/ui/hstack';
import { Text } from '@/components/ui/text';
import { ScrollView } from 'react-native-gesture-handler';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import React, { useState, useEffect, useCallback } from 'react';
import i18n from 'i18n-js';
import { IconM } from '../../components/media/IconsV2';

import app from '../../app.json';
import ModalColor from '../modals/ModalColor';
import { useDispatch, useSelector } from 'react-redux';
import { backendRequest, saveSettings } from '../../stores/thunks';
import FloatingLabelInput from '../../components/basic/FloatingLabelInput';
import Header from '../../components/basic/Header';
import Footer from '../../components/basic/Footer';
import BackGesture from '../../containers/BackGesture';
import PressableElement from '../../components/basic/PressableElement';
import SwitchableElement from '../../components/basic/SwitchableElement';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import ModalListFormat from '../modals/ModalListFormat';
import ModalVoices from '../modals/ModalVoices';
import InfoElement from '../../components/basic/InfoElement';
import { Platform } from 'react-native';
import { changeSettings } from '../../stores/mainSlice';

import { useFocusEffect } from '@react-navigation/native';

const currentColor = (color) => {
    switch (color) {
        case '0':
        case 0:
            return i18n.t('modalColor.blue');
        case '1':
        case 1:
            return i18n.t('modalColor.green');
        case '2':
        case 2:
            return i18n.t('modalColor.yellow');
        case '3':
        case 3:
            return i18n.t('modalColor.orange');
        case '4':
        case 4:
            return i18n.t('modalColor.red');
        default:
            return '';
    }
};

export default function AppSettings({ route, navigation }) {
    const dispatch = useDispatch();

    const insets = useSafeAreaInsets();

    const settings = useSelector((state) => state.settings);
    const user = useSelector((state) => state.user);
    const [showModal, setShowModal] = useState(false);
    const [showFormatModal, setShowFormatModal] = useState(false);
    const [showVoicesModal, setShowVoicesModal] = useState(false);
    const [contactTel, setContactTel] = useState(settings.contactTel);
    const size = useSelector((state) => JSON.stringify(state).length);
    const version = useSelector((state) => state.version);
    const commit = useSelector((state) => state.commit || '-');
    const [clicks, setClicks] = useState(0);

    const internal = __DEV__ || user.isAdmin || (!user.demo && (user.mail?.endsWith('@ina.coach') || user.mail?.endsWith('@bos-connect.de')));

    // useIsMounted no longer works - navigation does not unmount the component 
    useFocusEffect(
        useCallback(() => {
            console.log('FOCUS ON');
            return () => {
                console.log('FOCUS OFF');
                dispatch(saveSettings({ contactTel: contactTel, perf: clicks == 0 ? settings.perf : clicks > 3 }));
            };
        }, [])
    );

    const toggleHidePhone = () => {
        dispatch(changeSettings({ settings: { hidePhoneButton: !settings.hidePhoneButton } }));
    };

    return (
        <BackGesture navigation={navigation}>
            <VStack className="flex-1 bg-secondary-50">
                <Header navigation={navigation} options={{ title: i18n.t('sideNavigationDrawer.appSettings') }} />
                <View className="flex-1 bg-secondary-50">
                    <ScrollView scrollIndicatorInsets={{ right: 1 }} contentContainerStyle={{ paddingBottom: 90 + insets.bottom }} className="flex-1 bg-secondary-50 py-3 px-3">
                        <VStack space="xl">
                            <VStack space="md">
                                <SwitchableElement header={i18n.t('appSettings.contact')} mainText={i18n.t('appSettings.telButton')} isChecked={!settings.hidePhoneButton} onToggle={() => toggleHidePhone()} />
                                <FormControl className="px-3">
                                    <FloatingLabelInput label={i18n.t('appSettings.telButtonContact')} placeholder={i18n.t('appSettings.telButtonContact')} value={contactTel} onChangeText={setContactTel} />
                                    <FormControlHelperText className="mt-1">
                                        <HStack space="xs">
                                            <IconM className={`${Platform.OS == 'web' ? 'pt-0' : 'pt-1.5'} text-xs text-secondary-900`} name="info-outline" />
                                            <Text onPress={() => setClicks(clicks + 1)} className="text-secondary-900">
                                                {i18n.t('appSettings.helperText')}
                                            </Text>
                                        </HStack>
                                    </FormControlHelperText>
                                </FormControl>
                            </VStack>
                            <VStack space="md">
                                <PressableElement header={i18n.t('appSettings.colorContrast')} mainText={i18n.t('appSettings.color')} secondaryText={currentColor(settings.inAColor)} pressed={() => setShowModal(true)} />
                                <PressableElement header={i18n.t('appSettings.listFormat')} mainText={i18n.t('appSettings.view')} secondaryText={i18n.t(settings.rasterView ? 'modalFormat.raster' : 'modalFormat.list')} pressed={() => setShowFormatModal(true)} />
                                <SwitchableElement header={i18n.t('appSettings.media')} mainText={i18n.t('appSettings.crop')} isChecked={settings.imageCrop} onToggle={() => dispatch(changeSettings({ settings: { imageCrop: !settings.imageCrop } }))} />
                                <SwitchableElement mainText={i18n.t('appSettings.autoplay')} isChecked={settings.autoPlay} onToggle={() => dispatch(changeSettings({ settings: { autoPlay: !settings.autoPlay } }))} />
                                <SwitchableElement mainText={i18n.t('appSettings.showInA')} isChecked={settings.showInA == undefined || settings.showInA} onToggle={() => dispatch(changeSettings({ settings: { showInA: !(settings.showInA == undefined || settings.showInA), showSpecials: !(settings.showInA == undefined || settings.showInA) } }))} />
                                <SwitchableElement mainText={i18n.t('appSettings.showSpecials')} isChecked={settings.showSpecials == undefined || settings.showSpecials} onToggle={() => dispatch(changeSettings({ settings: { showSpecials: !(settings.showSpecials == undefined || settings.showSpecials) } }))} />
                                <SwitchableElement header={i18n.t('appSettings.language')} mainText={i18n.t('appSettings.speech')} isChecked={settings.speechEnabled} onToggle={() => dispatch(changeSettings({ settings: { speechEnabled: !settings.speechEnabled } }))} />
                                <PressableElement mainText={i18n.t('appSettings.voice')} secondaryText={settings.voice ? settings.voice.name : 'Standard'} pressed={() => setShowVoicesModal(true)} />
                                <InfoElement header={i18n.t('appSettings.misc')} mainText={i18n.t('appSettings.version')} secondaryText={'v' + app.expo.version} />
                                {internal && <SwitchableElement header={i18n.t('appSettings.internal')} mainText={i18n.t('appSettings.preview')} isChecked={settings.preview} onToggle={() => dispatch(changeSettings({ settings: { preview: !settings.preview } }))} />}
                                {internal && <InfoElement mainText={i18n.t('appSettings.commit')} secondaryText={commit} />}
                                {internal && <InfoElement mainText={i18n.t('appSettings.stateVersion')} secondaryText={'v' + version} />}
                                {internal && <InfoElement mainText={i18n.t('appSettings.stateSize')} secondaryText={size / 1000} />}
                                {user.serverVersion && <InfoElement mainText={i18n.t('appSettings.serverVersion')} secondaryText={'v' + user.serverVersion} />}
                            </VStack>
                        </VStack>
                    </ScrollView>
                    <ModalColor showModal={showModal} setShowModal={setShowModal} />
                    <ModalListFormat showModal={showFormatModal} setShowModal={setShowFormatModal} />
                    <ModalVoices showModal={showVoicesModal} setShowModal={setShowVoicesModal} />
                </View>
                <Footer navigation={navigation} />
            </VStack>
        </BackGesture>
    );
}
